// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Height maths for the hole view, kept pure so the sign convention — the one thing that is
// easy to get backwards — is asserted rather than eyeballed.
//
// A hole's profile is a line of stations along the playing line, tee end first, each
// carrying the terrain height under it (course-analysis-service samples them from the
// Lantmateriet 1 m height model). Reading a height therefore means projecting a position
// onto that line and interpolating between the two stations either side, which is what lets
// the same profile answer for the tee, for the green, and for wherever the player is
// standing right now.

import type { LatLon } from './tileMath';
import { distanceMeters, projectOntoSegment } from './tileMath';

export type ElevationPoint = LatLon & { elevationM: number };

export type HoleElevationProfile = {
  holeNumber: number;
  /** Stations along the playing line, tee end first. */
  points: ElevationPoint[];
};

export type ElevationDescriptor = 'uphill' | 'downhill' | 'level';

export type HeightDifference = {
  /** Positive when the target is above the origin — the shot plays uphill. */
  deltaM: number;
  descriptor: ElevationDescriptor;
};

/**
 * Below this a shot is called level.
 *
 * A metre either way changes no one's club, and it is inside what the height model and a
 * phone's own position are worth together — showing "1 m uphill" would be reporting noise
 * as if it were information.
 */
export const LEVEL_THRESHOLD_M = 1.5;

/**
 * How far off the playing line a position can be and still read from the profile.
 *
 * The profile only knows the ground along the centre of the hole. Someone in the trees a
 * long way offline may be standing well above or below it, so past this the honest answer
 * is that there is no reading — not the centreline's height quietly stood in for theirs.
 */
export const PROFILE_REACH_M = 80;

/**
 * Terrain height in metres at a position, or null when the profile cannot speak for it.
 *
 * Positions before the first station or past the last read as those stations rather than as
 * nothing: a player behind the tee or on the back of the green is still on the hole, and the
 * ground does not stop at the ends of the line.
 */
export const elevationAt = (
  profile: HoleElevationProfile,
  position: LatLon
): number | null => {
  const { points } = profile;
  if (points.length === 0) return null;
  if (points.length === 1) {
    return distanceMeters(points[0], position) <= PROFILE_REACH_M
      ? points[0].elevationM
      : null;
  }

  let nearest: { distanceMeters: number; elevationM: number } | null = null;
  for (let index = 0; index < points.length - 1; index += 1) {
    const from = points[index];
    const to = points[index + 1];
    const { t, distanceMeters } = projectOntoSegment(position, from, to);
    if (nearest !== null && distanceMeters >= nearest.distanceMeters) continue;
    nearest = {
      distanceMeters,
      elevationM: from.elevationM + (to.elevationM - from.elevationM) * t,
    };
  }

  if (nearest === null || nearest.distanceMeters > PROFILE_REACH_M) return null;
  return nearest.elevationM;
};

/** Level, uphill or downhill — the word, from the metres. */
export const describeElevation = (deltaM: number): ElevationDescriptor => {
  if (Math.abs(deltaM) < LEVEL_THRESHOLD_M) return 'level';
  return deltaM > 0 ? 'uphill' : 'downhill';
};

/**
 * The climb or drop from one position to another along the hole, or null when either end
 * falls outside what the profile covers.
 *
 * The sign is from the golfer's point of view: positive means the target sits above them and
 * the shot plays uphill.
 */
export const heightDifference = (
  profile: HoleElevationProfile,
  from: LatLon,
  to: LatLon
): HeightDifference | null => {
  const origin = elevationAt(profile, from);
  const target = elevationAt(profile, to);
  if (origin === null || target === null) return null;

  const deltaM = target - origin;
  return { deltaM, descriptor: describeElevation(deltaM) };
};
